import { useEffect, useMemo, useState } from 'react';
import { InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { AccessToken } from 'buzz_lib_ui';
import { ensure, registerBrowserAccessTokenProvider } from 'buzz_lib_shared';
import { useUserApi } from '../../hooks/useUserApi';
import { AuthLoadingComponent } from '../loaders/AuthLoadingComponent';
import { AccessTokenMissing } from '../unauthorized/AccessTokenMissing';
import { Unauthorised } from '../unauthorized/Unauthorised';
import { AuthContext } from './AuthContext';
import { jsx as _jsx } from "react/jsx-runtime";
export const AuthProvider = _ref => {
  let {
    authConfig,
    children
  } = _ref;
  const {
    instance,
    accounts,
    inProgress
  } = useMsal();
  const account = accounts.at(0);
  const [userGroups, setUserGroups] = useState([]);
  const [userMail, setUserMail] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [loadingMail, setLoadingMail] = useState(true);
  const [accessTokenMissing, setAccessTokenMissing] = useState(false);
  const user = useMemo(() => {
    return account ? mapAccountInfoToUser(account, userGroups) : null;
  }, [account, userGroups]);
  const userApi = useUserApi(authConfig.buzzAuthEndpoint);
  useEffect(() => {
    if (account) {
      loadUserGroups(account.localAccountId);
      loadUserMail(account.localAccountId);
    }
  }, [account]);
  const loadUserGroups = async userOid => {
    setLoadingGroups(true);
    const groups = await userApi.getUserGroups(userOid);
    setUserGroups(groups);
    setLoadingGroups(false);
  };
  const loadUserMail = async userOid => {
    setLoadingMail(true);
    const mail = await userApi.getUserMail(userOid);
    setUserMail(mail);
    setLoadingMail(false);
  };
  const login = () => {
    setLoading(true);
    instance.loginRedirect().finally(() => {
      setLoading(false);
    });
  };
  const passwordReset = () => {
    setLoading(true);
    instance.loginRedirect({
      authority: authConfig.passwordResetAuthority,
      scopes: [authConfig.graphScope]
    }).finally(() => {
      setLoading(false);
    });
  };
  const logout = () => {
    setLoading(true);
    setUserGroups([]);
    setUserMail('');
    instance.logoutRedirect();
    setLoading(false);
  };
  const acquireAccessToken = async () => {
    ensure(account, 'Cannot acquire access token: no authenticated account');
    const request = {
      scopes: [authConfig.clientCredentialsFlowScope],
      account
    };
    try {
      const result = await instance.acquireTokenSilent(request);

      // B2C responds without an access token (and with an empty scope) when the client app isn't granted the requested scope
      if (!result.accessToken) {
        setAccessTokenMissing(true);
        throw new Error("B2C returned no access token for scope \"".concat(authConfig.clientCredentialsFlowScope, "\". Check the app registration's API permissions and admin consent."));
      }
      return new AccessToken(result.accessToken);
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        await instance.acquireTokenRedirect(request);
      }
      throw error;
    }
  };
  registerBrowserAccessTokenProvider({
    getAccessToken: acquireAccessToken
  });
  if (inProgress !== InteractionStatus.None) {
    return /*#__PURE__*/_jsx(AuthLoadingComponent, {
      status: inProgress
    });
  }
  if (!user) {
    return /*#__PURE__*/_jsx(Unauthorised, {
      login: login,
      passwordReset: passwordReset
    });
  }
  if (accessTokenMissing) {
    return /*#__PURE__*/_jsx(AccessTokenMissing, {
      login: login,
      logout: logout
    });
  }
  const value = {
    user,
    userGroups,
    loading,
    loadingGroups,
    userMail,
    loadingMail,
    login,
    logout,
    passwordReset,
    getAccessToken: acquireAccessToken
  };
  return /*#__PURE__*/_jsx(AuthContext.Provider, {
    value: value,
    children: children
  });
};
const mapAccountInfoToUser = (account, userGroups) => {
  ensure(account.idTokenClaims, 'Id token is missing for authorized user');
  ensure(account.idTokenClaims.name, 'Id token is missing "name" claim');
  ensure(account.idTokenClaims.emails, 'Id token is missing "emails" claim');
  if (!account.idTokenClaims.emails.length) {
    throw new Error('Id token "emails" claim is missing email entries');
  }
  return {
    id: account.localAccountId,
    Username: account.idTokenClaims.name,
    Mail: account.idTokenClaims.emails[0],
    Groups: userGroups
  };
};